import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance, { clearAuthToken } from '@/lib/axios';
import { Plus, TrendingUp, TrendingDown, BarChart3, LogOut, Trash2, Home, Database, Users, FileText, Settings, Download, Briefcase } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import NotificationBell from '@/components/NotificationBell';
import ReferralModule from '@/components/ReferralModule';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Dashboard({ user }) {
  const navigate = useNavigate();
  const [simulations, setSimulations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSimulations, setSelectedSimulations] = useState([]);
  const [currentUser, setCurrentUser] = useState(user);

  useEffect(() => {
    loadSimulations();
    loadUserInfo();
  }, []);

  const loadUserInfo = async () => {
    try {
      const response = await axiosInstance.get(`${API}/auth/me`);
      setCurrentUser(response.data);
    } catch (error) {
      console.error('Error loading user info:', error);
    }
  };

  const loadSimulations = async () => {
    try {
      const response = await axiosInstance.get(`${API}/simulations`);
      setSimulations(response.data);
    } catch (error) {
      console.error('Error loading simulations:', error);
      toast.error('Erreur de chargement des simulations');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axiosInstance.post(`${API}/auth/logout`, {});
      clearAuthToken();
      navigate('/');
      toast.success('À bientôt !');
    } catch (error) {
      console.error('Logout error:', error);
      clearAuthToken();
      navigate('/');
    }
  };

  const handleDeleteSimulation = async (simulationId, e) => {
    e.stopPropagation();
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette simulation ?')) {
      return;
    }

    try {
      await axiosInstance.delete(`${API}/simulations/${simulationId}`);
      setSimulations(simulations.filter(s => s.simulation_id !== simulationId));
      setSelectedSimulations(selectedSimulations.filter(id => id !== simulationId));
      toast.success('Simulation supprimée');
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Erreur de suppression');
    }
  };

  const toggleSimulationSelection = (simulationId) => {
    setSelectedSimulations(prev => {
      if (prev.includes(simulationId)) {
        return prev.filter(id => id !== simulationId);
      } else {
        return [...prev, simulationId];
      }
    });
  };

  const handleCompare = () => {
    if (selectedSimulations.length < 2) {
      toast.error('Sélectionnez au moins 2 simulations à comparer');
      return;
    }
    navigate('/compare', { state: { simulationIds: selectedSimulations } });
  };

  const handleExportExcel = async (simulationId) => {
    try {
      const response = await axiosInstance.get(`${API}/simulations/${simulationId}/export-excel`, {
        withCredentials: true,
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `simulation-${simulationId}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Export Excel téléchargé !');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Erreur lors de l\'export Excel');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#030305] flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-cyan-500 border-t-transparent"></div>
          <p className="mt-4 text-cyan-400 font-['Rajdhani']">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 relative">
      <div className="relative z-10">
        {/* Header */}
        <header className="border-b border-slate-200 bg-white shadow-sm">
          <div className="container mx-auto px-6 py-4">
            <div className="flex items-center justify-between">
              <h1 className="text-xl font-bold text-slate-800">
                Achat Immo Gagnant
              </h1>
              <div className="flex items-center gap-4">
                {currentUser?.is_vip && (
                  <div className="flex items-center gap-2 px-3 py-1 bg-blue-50 border border-blue-200 rounded-full">
                    <span className="text-xs text-blue-600 font-semibold">VIP</span>
                    {currentUser?.credits !== undefined && currentUser?.credits !== -1 && (
                      <span className="text-xs text-blue-800 font-bold">
                        {currentUser.credits} crédit{currentUser.credits !== 1 ? 's' : ''}
                      </span>
                    )}
                    {currentUser?.credits === -1 && (
                      <span className="text-xs text-blue-800">∞</span>
                    )}
                  </div>
                )}
                {currentUser?.is_super_admin && (
                  <div className="flex items-center gap-2 px-3 py-1 bg-amber-50 border border-amber-200 rounded-full">
                    <Settings className="h-3 w-3 text-amber-600" />
                    <span className="text-xs text-amber-600 font-semibold">Admin</span>
                  </div>
                )}
                <div className="flex items-center gap-3">
                  {currentUser?.picture && (
                    <img
                      src={currentUser.picture}
                      alt={currentUser.name}
                      className="h-10 w-10 rounded-full border-2 border-slate-200"
                    />
                  )}
                  <span className="text-sm text-slate-600">{currentUser?.name}</span>
                </div>
                <NotificationBell />
                <Button
                  onClick={handleLogout}
                  data-testid="logout-button"
                  variant="ghost"
                  size="sm"
                  className="text-slate-500 hover:text-slate-700 hover:bg-slate-100"
                >
                  <LogOut className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="container mx-auto px-6 py-12">
          {/* Actions Bar */}
          <div className="flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center mb-8">
            <div>
              <h2 className="text-2xl font-bold text-slate-800 mb-2">
                Tableau de Bord
              </h2>
              <p className="text-slate-500">Vos simulations d'investissement immobilier</p>
            </div>
            <div className="flex gap-3 flex-wrap">
              <Button
                onClick={() => navigate('/properties')}
                data-testid="properties-button"
                className="bg-slate-600 hover:bg-slate-700 text-white"
              >
                <Home className="mr-2 h-4 w-4" />
                Mes Biens
              </Button>
              <Button
                onClick={() => navigate('/market-data')}
                data-testid="market-data-button"
                className="bg-amber-500 hover:bg-amber-600 text-white"
              >
                <Database className="mr-2 h-4 w-4" />
                Données Marché
              </Button>
              {currentUser?.is_super_admin && (
                <Button
                  onClick={() => navigate('/admin')}
                  data-testid="admin-button"
                  className="bg-purple-600 hover:bg-purple-700 text-white"
                >
                  <Users className="mr-2 h-4 w-4" />
                  Administration
                </Button>
              )}
              {currentUser?.is_vip && (
                <Button
                  onClick={() => navigate('/conseiller-pro')}
                  data-testid="conseiller-pro-button"
                  className="bg-indigo-600 hover:bg-indigo-700 text-white"
                >
                  <Briefcase className="mr-2 h-4 w-4" />
                  Mode Pro
                </Button>
              )}
              {selectedSimulations.length >= 2 && (
                <Button
                  onClick={handleCompare}
                  data-testid="compare-button"
                  className="bg-indigo-600 hover:bg-indigo-700 text-white"
                >
                  <BarChart3 className="mr-2 h-4 w-4" />
                  Comparer ({selectedSimulations.length})
                </Button>
              )}
              <Button
                onClick={() => navigate('/simulation/new')}
                data-testid="new-simulation-button"
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                <Plus className="mr-2 h-4 w-4" />
                Nouvelle simulation
              </Button>
            </div>
          </div>

          {/* Simulations Grid */}
          {simulations.length === 0 ? (
            <div className="bg-white border border-slate-200 rounded-xl p-12 text-center" data-testid="empty-state">
              <div className="max-w-md mx-auto">
                <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-blue-50 flex items-center justify-center">
                  <BarChart3 className="h-10 w-10 text-blue-500" />
                </div>
                <h3 className="text-xl font-semibold text-slate-800 mb-2">Aucune simulation</h3>
                <p className="text-slate-500 mb-6">
                  Commencez votre première analyse d'investissement immobilier
                </p>
                <Button
                  onClick={() => navigate('/simulation/new')}
                  data-testid="empty-new-simulation-button"
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Créer une simulation
                </Button>
              </div>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {simulations.map((sim) => (
                <div
                  key={sim.simulation_id}
                  data-testid={`simulation-card-${sim.simulation_id}`}
                  className={`bg-white border rounded-xl p-6 cursor-pointer transition-all duration-200 hover:shadow-md ${
                    selectedSimulations.includes(sim.simulation_id)
                      ? 'border-blue-500 ring-2 ring-blue-100'
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                  onClick={() => navigate(`/simulation/${sim.simulation_id}`)}
                >
                  {/* Selection checkbox */}
                  <div className="flex justify-between items-start mb-4">
                    <input
                      type="checkbox"
                      checked={selectedSimulations.includes(sim.simulation_id)}
                      onChange={() => toggleSimulationSelection(sim.simulation_id)}
                      onClick={(e) => e.stopPropagation()}
                      data-testid={`checkbox-${sim.simulation_id}`}
                      className="h-5 w-5 rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                    />
                    <div className="flex items-center gap-1">
                      <Button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleExportExcel(sim.simulation_id);
                        }}
                        data-testid={`export-${sim.simulation_id}`}
                        variant="ghost"
                        size="sm"
                        className="text-blue-400 hover:text-blue-500 hover:bg-blue-50"
                        title="Exporter Excel"
                      >
                        <Download className="h-4 w-4" />
                      </Button>
                      <Button
                        onClick={(e) => handleDeleteSimulation(sim.simulation_id, e)}
                        data-testid={`delete-${sim.simulation_id}`}
                        variant="ghost"
                        size="sm"
                        className="text-red-400 hover:text-red-500 hover:bg-red-50"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>

                  {/* Location */}
                  <h3 className="text-lg font-semibold text-slate-800 mb-2">
                    {sim.location}
                  </h3>

                  {/* Property details */}
                  <div className="grid grid-cols-2 gap-2 mb-4 text-sm">
                    <div className="text-slate-500">
                      Prix: <span className="text-slate-800 font-semibold">{sim.property_price.toLocaleString()} €</span>
                    </div>
                    <div className="text-slate-500">
                      {sim.surface_m2} m² - {sim.nb_rooms}P
                    </div>
                    <div className="text-slate-500">
                      Prêt: <span className="text-slate-800">{sim.loan_type}%</span>
                    </div>
                    <div className="text-slate-500">
                      Durée: <span className="text-slate-800">{sim.loan_duration} ans</span>
                    </div>
                  </div>

                  {/* Cash flows */}
                  <div className="space-y-2 border-t border-slate-100 pt-4">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-slate-500">Location trad.</span>
                      <span className={`flex items-center gap-1 font-semibold ${
                        sim.cashflow_traditional > 0 ? 'text-green-600' : 'text-red-500'
                      }`}>
                        {sim.cashflow_traditional > 0 ? (
                          <TrendingUp className="h-4 w-4" />
                        ) : (
                          <TrendingDown className="h-4 w-4" />
                        )}
                        {sim.cashflow_traditional.toLocaleString()} €/mois
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-slate-500">Airbnb</span>
                      <span className={`flex items-center gap-1 font-semibold ${
                        sim.cashflow_airbnb > 0 ? 'text-green-600' : 'text-red-500'
                      }`}>
                        {sim.cashflow_airbnb > 0 ? (
                          <TrendingUp className="h-4 w-4" />
                        ) : (
                          <TrendingDown className="h-4 w-4" />
                        )}
                        {sim.cashflow_airbnb.toLocaleString()} €/mois
                      </span>
                    </div>
                  </div>

                  {/* Date */}
                  <div className="mt-4 pt-4 border-t border-slate-100 text-xs text-slate-400">
                    {new Date(sim.created_at).toLocaleDateString('fr-FR')}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Referral Module - After simulations */}
          <div className="mt-8">
            <ReferralModule user={currentUser} />
          </div>
        </main>
      </div>
    </div>
  );
}
